// Operator triage view for public demo-access requests.
//
// Approve mints a single-use token (TTL chosen per-request) and emails it
// to the requester; the plaintext is also returned in the response so the
// operator can copy-paste it manually if SMTP is misconfigured. After
// closing the approve dialog the plaintext is gone — re-issue means
// revoke + approve again.

import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { CheckCircle2, Copy, Inbox, Slash, XCircle } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { ApiError, demoAdmin, type DemoRequestRow } from '@/api/client';
import { Empty, ErrorBox } from '@/components/empty';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';

export const Route = createFileRoute('/_authed/demo-requests')({
  component: DemoRequestsPage,
});

type StatusTab = 'pending' | 'approved' | 'rejected';

function DemoRequestsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<StatusTab>('pending');
  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <div className="inline-flex items-center gap-2 rounded-full bg-accent px-3 py-1 text-xs font-medium text-accent-foreground">
          <Inbox className="h-3.5 w-3.5" />
          {t('demo_admin.eyebrow')}
        </div>
        <h1 className="text-3xl font-semibold tracking-tight">{t('demo_admin.title')}</h1>
        <p className="max-w-3xl text-muted-foreground">{t('demo_admin.subtitle')}</p>
      </header>

      <Tabs value={tab} onValueChange={(v) => setTab(v as StatusTab)}>
        <TabsList>
          <TabsTrigger value="pending">{t('demo_admin.tab_pending')}</TabsTrigger>
          <TabsTrigger value="approved">{t('demo_admin.tab_approved')}</TabsTrigger>
          <TabsTrigger value="rejected">{t('demo_admin.tab_rejected')}</TabsTrigger>
        </TabsList>
        <TabsContent value="pending">
          <RequestList status="pending" />
        </TabsContent>
        <TabsContent value="approved">
          <RequestList status="approved" />
        </TabsContent>
        <TabsContent value="rejected">
          <RequestList status="rejected" />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function RequestList({ status }: { status: StatusTab }) {
  const { t } = useTranslation();
  const list = useQuery({
    queryKey: ['demo-requests', status],
    queryFn: () => demoAdmin.list(status),
  });

  if (list.isError) return <ErrorBox message={t('common.error')} />;
  if (list.isLoading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }
  if (!list.data?.length) {
    return <Empty title={t('demo_admin.empty')} />;
  }

  return (
    <div className="grid gap-3">
      {list.data.map((row) => (
        <RequestCard key={row.id} row={row} />
      ))}
    </div>
  );
}

function RequestCard({ row }: { row: DemoRequestRow }) {
  const { t } = useTranslation();
  const [approveOpen, setApproveOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="text-base">{row.name}</CardTitle>
            <CardDescription className="font-mono text-xs">{row.email}</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge row={row} />
            <span className="text-xs text-muted-foreground">
              {dayjs(row.created_at).format('YYYY-MM-DD HH:mm')}
            </span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        <dl className="grid gap-x-4 gap-y-1.5 sm:grid-cols-2">
          {row.company && (
            <Row label={t('demo_admin.field_company')} value={row.company} />
          )}
          {row.phone && <Row label={t('demo_admin.field_phone')} value={row.phone} mono />}
          {row.use_case && (
            <div className="sm:col-span-2 space-y-1">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('demo_admin.field_use_case')}
              </dt>
              <dd className="whitespace-pre-wrap text-sm leading-relaxed">{row.use_case}</dd>
            </div>
          )}
          {row.rejection_reason && (
            <div className="sm:col-span-2 space-y-1">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {t('demo_admin.field_rejection_reason')}
              </dt>
              <dd className="text-sm text-amber-600 dark:text-amber-400">
                {row.rejection_reason}
              </dd>
            </div>
          )}
          {row.token && (
            <div className="sm:col-span-2">
              <TokenStatus token={row.token} />
            </div>
          )}
        </dl>

        <div className="flex flex-wrap gap-2 pt-1">
          {row.status === 'pending' && (
            <>
              <Button size="sm" onClick={() => setApproveOpen(true)}>
                <CheckCircle2 className="me-1 h-3.5 w-3.5" />
                {t('demo_admin.approve')}
              </Button>
              <Button size="sm" variant="outline" onClick={() => setRejectOpen(true)}>
                <XCircle className="me-1 h-3.5 w-3.5" />
                {t('demo_admin.reject')}
              </Button>
            </>
          )}
          {row.status === 'approved' && row.token && !row.token.revoked_at && !row.token.used_at && (
            <RevokeButton requestId={row.id} />
          )}
        </div>
      </CardContent>

      {approveOpen && (
        <ApproveDialog row={row} onClose={() => setApproveOpen(false)} />
      )}
      {rejectOpen && (
        <RejectDialog row={row} onClose={() => setRejectOpen(false)} />
      )}
    </Card>
  );
}

function StatusBadge({ row }: { row: DemoRequestRow }) {
  const { t } = useTranslation();
  if (row.status === 'pending') return <Badge variant="secondary">{t('demo_admin.status_pending')}</Badge>;
  if (row.status === 'rejected') return <Badge variant="destructive">{t('demo_admin.status_rejected')}</Badge>;

  const tk = row.token;
  if (tk?.revoked_at) return <Badge variant="outline">{t('demo_admin.status_revoked')}</Badge>;
  if (tk?.used_at) return <Badge variant="success">{t('demo_admin.status_used')}</Badge>;
  if (tk && new Date(tk.expires_at) < new Date()) {
    return <Badge variant="outline">{t('demo_admin.status_expired')}</Badge>;
  }
  return <Badge variant="success">{t('demo_admin.status_approved')}</Badge>;
}

function TokenStatus({ token }: { token: NonNullable<DemoRequestRow['token']> }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-md border border-border bg-muted/30 p-3 text-xs space-y-1 font-mono">
      <div className="flex justify-between">
        <span className="text-muted-foreground">{t('demo_admin.token_issued')}</span>
        <span>{dayjs(token.issued_at).format('YYYY-MM-DD HH:mm')}</span>
      </div>
      <div className="flex justify-between">
        <span className="text-muted-foreground">{t('demo_admin.token_expires')}</span>
        <span>{dayjs(token.expires_at).format('YYYY-MM-DD HH:mm')}</span>
      </div>
      {token.used_at && (
        <div className="flex justify-between">
          <span className="text-muted-foreground">{t('demo_admin.token_used')}</span>
          <span>{dayjs(token.used_at).format('YYYY-MM-DD HH:mm')}</span>
        </div>
      )}
      {token.revoked_at && (
        <div className="flex justify-between text-amber-600 dark:text-amber-400">
          <span>{t('demo_admin.token_revoked')}</span>
          <span>{dayjs(token.revoked_at).format('YYYY-MM-DD HH:mm')}</span>
        </div>
      )}
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="space-y-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className={mono ? 'font-mono text-sm' : 'text-sm'}>{value}</dd>
    </div>
  );
}

function ApproveDialog({ row, onClose }: { row: DemoRequestRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ttlHours, setTtlHours] = useState(24);
  const [issued, setIssued] = useState<{ token: string; expires_at: string } | null>(null);

  const approve = useMutation({
    mutationFn: () => demoAdmin.approve(row.id, ttlHours),
    onSuccess: (resp) => {
      setIssued({ token: resp.token, expires_at: resp.expires_at });
      qc.invalidateQueries({ queryKey: ['demo-requests'] });
      toast.success(t('demo_admin.approve_toast'));
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('demo_admin.approve_title', { name: row.name })}</DialogTitle>
          <DialogDescription>{t('demo_admin.approve_subtitle')}</DialogDescription>
        </DialogHeader>

        {issued ? (
          <IssuedTokenView issued={issued} onClose={onClose} />
        ) : (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              approve.mutate();
            }}
            className="space-y-4"
          >
            <div className="space-y-1.5">
              <Label htmlFor="ttl">{t('demo_admin.ttl_label')}</Label>
              <Input
                id="ttl"
                type="number"
                min={1}
                max={168}
                value={ttlHours}
                onChange={(e) => setTtlHours(Math.max(1, Math.min(168, Number(e.target.value) || 24)))}
              />
              <p className="text-xs text-muted-foreground">{t('demo_admin.ttl_help')}</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={approve.isPending}>
                {approve.isPending ? t('common.saving') : t('demo_admin.approve_confirm')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

function IssuedTokenView({
  issued,
  onClose,
}: {
  issued: { token: string; expires_at: string };
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const link = `${window.location.origin}/demo/login?token=${encodeURIComponent(issued.token)}`;
  const copy = (val: string, label: string) => {
    navigator.clipboard.writeText(val).then(
      () => toast.success(t('common.copied', { what: label })),
      () => toast.error(t('common.copy_failed')),
    );
  };
  return (
    <div className="space-y-3 text-sm">
      <p className="text-muted-foreground">{t('demo_admin.issued_body')}</p>
      <div className="space-y-1.5">
        <Label>{t('demo_admin.issued_link_label')}</Label>
        <div className="flex gap-2">
          <Input readOnly value={link} className="font-mono text-xs" />
          <Button type="button" size="icon" variant="outline" onClick={() => copy(link, 'link')}>
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <div className="space-y-1.5">
        <Label>{t('demo_admin.issued_token_label')}</Label>
        <div className="flex gap-2">
          <Input readOnly value={issued.token} className="font-mono text-xs" />
          <Button
            type="button"
            size="icon"
            variant="outline"
            onClick={() => copy(issued.token, 'token')}
          >
            <Copy className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <p className="text-xs text-muted-foreground">
        {t('demo_admin.issued_expires', {
          when: dayjs(issued.expires_at).format('YYYY-MM-DD HH:mm'),
        })}
      </p>
      <DialogFooter>
        <Button type="button" onClick={onClose}>
          {t('common.close')}
        </Button>
      </DialogFooter>
    </div>
  );
}

function RejectDialog({ row, onClose }: { row: DemoRequestRow; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');

  const reject = useMutation({
    mutationFn: () => demoAdmin.reject(row.id, reason.trim() || null),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demo-requests'] });
      toast.success(t('demo_admin.reject_toast'));
      onClose();
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : (e as Error).message),
  });

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('demo_admin.reject_title', { name: row.name })}</DialogTitle>
          <DialogDescription>{t('demo_admin.reject_subtitle')}</DialogDescription>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            reject.mutate();
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="reason">{t('demo_admin.reason_label')}</Label>
            <textarea
              id="reason"
              className="flex min-h-[80px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              maxLength={500}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t('demo_admin.reason_placeholder') as string}
            />
            <p className="text-xs text-muted-foreground">{t('demo_admin.reason_help')}</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" variant="destructive" disabled={reject.isPending}>
              {reject.isPending ? t('common.saving') : t('demo_admin.reject_confirm')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RevokeButton({ requestId }: { requestId: string }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: () => demoAdmin.revoke(requestId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['demo-requests'] });
      toast.success(t('demo_admin.revoke_toast'));
    },
    onError: (e) => toast.error(e instanceof ApiError ? e.message : (e as Error).message),
  });
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={revoke.isPending}
      onClick={() => revoke.mutate()}
    >
      <Slash className="me-1 h-3.5 w-3.5" />
      {t('demo_admin.revoke')}
    </Button>
  );
}
